import { createFileRoute, Link } from "@tanstack/react-router";
import portrait from "@/assets/mark-portrait.jpg";
import { AchievementCard } from "@/components/coach/AchievementCard";
import { EmphasizedTitle } from "@/components/site/EmphasizedTitle";
import {
  getMarkEglyBioParagraphs,
  getMarkEglyCareerHighlights,
  getMarkEglyCompetitiveAchievements,
  getMarkEglyGrowthBullets,
  getMarkEglyGrowthIntro,
  getMarkEglyNotableStudents,
  getMarkEglyResilience,
  getMarkEglyTeachingHonorGroups,
} from "@/lib/mark-egly-profile";
import { pageHead, personJsonLd } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";
import { ArrowRight, Award } from "lucide-react";

export const Route = createFileRoute("/about")({
  head: () =>
    pageHead({
      title: m.about_meta_title(),
      description: m.about_meta_description(),
      path: "/about",
      image: portrait,
      jsonLd: personJsonLd(),
    }),
  component: AboutPage,
});

function AboutPage() {
  const careerHighlights = getMarkEglyCareerHighlights();
  const bioParagraphs = getMarkEglyBioParagraphs();
  const competitiveAchievements = getMarkEglyCompetitiveAchievements();
  const teachingHonorGroups = getMarkEglyTeachingHonorGroups();
  const growthIntro = getMarkEglyGrowthIntro();
  const growthBullets = getMarkEglyGrowthBullets();
  const notableStudents = getMarkEglyNotableStudents();
  const resilience = getMarkEglyResilience();

  return (
    <>
      <section className="bg-ink">
        <div className="container-page page-hero-pad">
          <span className="eyebrow">{m.about_eyebrow()}</span>
          <h1 className="mt-3 max-w-4xl font-display text-hero leading-[0.95] md:text-8xl">
            <EmphasizedTitle lead={m.about_title_lead()} accent={m.about_title_accent()} />
          </h1>
          <p className="mt-6 max-w-2xl text-foreground/75 md:text-lg">{m.about_hero_body()}</p>
        </div>
      </section>

      <section className="container-page page-follow-pad">
        <div className="grid items-start gap-14 md:grid-cols-5">
          <div className="md:col-span-2">
            <img
              src={portrait}
              alt={m.about_portrait_alt()}
              loading="lazy"
              width={895}
              height={1024}
              className="aspect-[4/5] w-full border border-white/10 object-cover"
            />
            <div className="mt-4 border-s-2 border-ember ps-4">
              <div className="font-display text-sm tracking-[0.18em] text-ember">Mark Egly</div>
              <div className="text-sm text-foreground/70">{m.about_pga_member()}</div>
            </div>
            <ul className="mt-6 space-y-2 text-sm text-foreground/70">
              {careerHighlights.map((item) => (
                <li key={item} className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ember-fill" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h2 className="font-display text-4xl">{m.about_heading()}</h2>
            {bioParagraphs.map((paragraph) => (
              <p key={paragraph.slice(0, 40)} className="mt-4 text-foreground/80">
                {paragraph}
              </p>
            ))}
            <Link
              to="/method"
              className="mt-6 inline-flex items-center gap-2 font-display text-sm tracking-[0.18em] text-ember hover:text-foreground"
            >
              {m.about_explore_method()} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
          </div>
        </div>
      </section>

      <section className="container-page pb-16 md:pb-20">
        <span className="eyebrow">{m.about_playing_eyebrow()}</span>
        <h2 className="mt-3 font-display text-4xl md:text-5xl">
          <EmphasizedTitle
            lead={m.about_playing_title_lead()}
            accent={m.about_playing_title_accent()}
          />
        </h2>
        <ul className="mt-8 grid gap-3 md:grid-cols-2">
          {competitiveAchievements.map((item) => (
            <AchievementCard key={item.title} title={item.title} detail={item.detail} />
          ))}
        </ul>
      </section>

      <section className="border-y border-white/5 bg-black/50">
        <div className="container-page page-section-pad">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-ember" />
            <span className="eyebrow">{m.about_honors_eyebrow()}</span>
          </div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">
            <EmphasizedTitle
              lead={m.about_honors_title_lead()}
              accent={m.about_honors_title_accent()}
            />
          </h2>
          <div className="mt-10 grid gap-10 md:grid-cols-3">
            {teachingHonorGroups.map((group) => (
              <div key={group.heading}>
                <h3 className="border-b border-white/10 pb-2 font-display text-lg text-ember">
                  {group.heading}
                </h3>
                <ul className="mt-4 space-y-4">
                  {group.items.map((item) => (
                    <li key={item.title}>
                      <div className="text-sm font-medium text-foreground">{item.title}</div>
                      {item.detail ? (
                        <div className="mt-0.5 text-sm text-foreground/65">{item.detail}</div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-page page-section-pad">
        <div className="grid gap-14 lg:grid-cols-2">
          <div>
            <span className="eyebrow">{m.about_growth_eyebrow()}</span>
            <h2 className="mt-3 font-display text-4xl">
              <EmphasizedTitle
                lead={m.about_growth_title_lead()}
                accent={m.about_growth_title_accent()}
              />
            </h2>
            <p className="mt-5 text-foreground/80">{growthIntro}</p>
            <ul className="mt-6 space-y-3 text-sm text-foreground/75">
              {growthBullets.map((item) => (
                <li key={item} className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ember-fill" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <span className="eyebrow">{m.about_students_eyebrow()}</span>
            <h2 className="mt-3 font-display text-4xl">
              <EmphasizedTitle
                lead={m.about_students_title_lead()}
                accent={m.about_students_title_accent()}
              />
            </h2>
            <div className="mt-8 grid gap-px bg-white/10">
              {notableStudents.map((student) => (
                <figure key={student.name} className="bg-ink p-6">
                  <figcaption className="font-display text-lg text-foreground">
                    {student.name}
                  </figcaption>
                  <p className="mt-2 text-sm text-foreground/70">{student.achievement}</p>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container-page pb-16 md:pb-20">
        <div className="border border-white/10 bg-black/40 p-8 md:p-10">
          <span className="eyebrow">{m.about_character_eyebrow()}</span>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">{m.about_character_title()}</h2>
          <p className="mt-5 max-w-3xl text-foreground/80">{resilience}</p>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/schedule" className="btn-ember btn-ember-hover">
            {m.about_book_with_mark()} <ArrowRight className="h-5 w-5 rtl:rotate-180" />
          </Link>
          <Link
            to="/method"
            className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 font-display text-sm tracking-[0.18em] text-foreground/90 hover:border-ember hover:text-ember"
          >
            {m.about_learn_method()} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </Link>
        </div>
      </section>
    </>
  );
}
